/**
 * Save Migrations
 * Versioned migration system for save data upgrades.
 *
 * Each migration runs once when loading a save with a version
 * lower than the migration's version number. Migrations run
 * in order (lowest version first) and mutate saveData in place.
 *
 * To add a new migration:
 * 1. Increment CURRENT_SAVE_VERSION
 * 2. Add a new entry to the MIGRATIONS array
 * 3. Add tests in src/__tests__/sprint5/saveMigrations.test.ts
 */

export interface SaveMigration {
  version: number;
  description: string;
  migrate: (saveData: any) => void;
}

/**
 * The latest save format version. Saved alongside every save
 * and compared on load to determine which migrations to run.
 */
export const CURRENT_SAVE_VERSION = 8;

export const MIGRATIONS: SaveMigration[] = [
  {
    version: 2,
    description: 'Add hauler buddies, auto-sell settings, fish discovery',
    migrate: (data) => {
      if (!data.playerState.haulerBuddies) {
        data.playerState.haulerBuddies = [];
      }
      if (!data.playerState.autoSellSettings) {
        data.playerState.autoSellSettings = {
          protectedFish: {},
        };
      }
      if (!data.playerState.discoveredFish) {
        data.playerState.discoveredFish = [];
      }
      // Add records to fish discoveries if missing
      if (data.playerState.discoveredFish && Array.isArray(data.playerState.discoveredFish)) {
        data.playerState.discoveredFish.forEach((discovery: any) => {
          if (!discovery.records) {
            discovery.records = {
              maxLength: 0,
              maxWeight: 0,
              bestQuality: 'poor',
            };
          }
        });
      }
    },
  },
  {
    version: 3,
    description: 'Add equipment quantity, quest state, acceptedQuestDefinitions',
    migrate: (data) => {
      // Add quantity to owned equipment if missing
      if (data.playerState.ownedEquipment) {
        Object.keys(data.playerState.ownedEquipment).forEach((equipId) => {
          const item = data.playerState.ownedEquipment[equipId];
          if (item && typeof item.quantity === 'undefined') {
            item.quantity = 1;
          }
        });
      }
      // Add quest state if missing
      if (!data.playerState.quests) {
        data.playerState.quests = {
          activeQuests: [],
          completedQuests: [],
          failedQuests: [],
          availableQuests: [],
          lastRefresh: Date.now(),
          acceptedQuestDefinitions: {},
        };
      }
      // Add acceptedQuestDefinitions if missing
      if (!data.playerState.quests.acceptedQuestDefinitions) {
        data.playerState.quests.acceptedQuestDefinitions = {};
      }
    },
  },
  {
    version: 4,
    description: 'Add shop state, earnedMilestones, statsByZone',
    migrate: (data) => {
      if (!data.playerState.shops) {
        data.playerState.shops = {
          unlockedOutfitters: ['lakeside_tackle'],
          unlockedMarkets: ['camp_chef'],
          visitedShops: {},
          purchaseHistory: [],
        };
      }
      if (!data.playerState.earnedMilestones) {
        data.playerState.earnedMilestones = [];
      }
      if (data.playerState.stats && !data.playerState.stats.statsByZone) {
        data.playerState.stats.statsByZone = {};
      }
    },
  },
  {
    version: 5,
    description: 'Fix null currency, add LINE equipment, rename lure to tackle',
    migrate: (data) => {
      // Fix null/undefined currency
      if (data.playerState.currency === null || data.playerState.currency === undefined) {
        data.playerState.currency = 0;
      }
      // Add LINE equipment if missing
      if (data.playerState.equippedGear && data.playerState.equippedGear.line === undefined) {
        if (data.playerState.ownedEquipment && !data.playerState.ownedEquipment.line_mono) {
          data.playerState.ownedEquipment.line_mono = { ownedAt: Date.now(), quantity: 1, timesUsed: 0 };
        }
        data.playerState.equippedGear.line = 'line_mono';
      }
      // Rename lure -> tackle slot
      if (data.playerState.equippedGear && data.playerState.equippedGear.lure !== undefined) {
        const oldLureValue = data.playerState.equippedGear.lure;
        data.playerState.equippedGear.tackle = oldLureValue;
        delete data.playerState.equippedGear.lure;
      }
      // Add tackle slot if missing (very old saves)
      if (data.playerState.equippedGear && data.playerState.equippedGear.tackle === undefined) {
        if (data.playerState.ownedEquipment && !data.playerState.ownedEquipment.bobber_basic) {
          data.playerState.ownedEquipment.bobber_basic = { ownedAt: Date.now(), quantity: 1, timesUsed: 0 };
        }
        data.playerState.equippedGear.tackle = 'bobber_basic';
      }
    },
  },
  {
    version: 6,
    description: 'Add free bobber_basic fallback, upgrade bait_worms quantity',
    migrate: (data) => {
      // Give free bobber_basic to players who had lure_spinner equipped
      if (data.playerState.equippedGear && data.playerState.equippedGear.tackle === 'lure_spinner' &&
          data.playerState.ownedEquipment && !data.playerState.ownedEquipment.bobber_basic) {
        data.playerState.ownedEquipment.bobber_basic = { ownedAt: Date.now(), quantity: 1, timesUsed: 0 };
      }
      // Upgrade bait_worms from qty 1 to 10 for existing saves
      if (data.playerState.ownedEquipment && data.playerState.ownedEquipment.bait_worms && data.playerState.ownedEquipment.bait_worms.quantity === 1) {
        data.playerState.ownedEquipment.bait_worms.quantity = 10;
      }
    },
  },
  {
    version: 7,
    description: 'Add fishing buddy equippedGear, rename lure to tackle on buddies',
    migrate: (data) => {
      if (data.playerState.fishingBuddies && Array.isArray(data.playerState.fishingBuddies)) {
        data.playerState.fishingBuddies.forEach((buddy: any) => {
          if (!buddy.equippedGear) {
            buddy.equippedGear = { rod: null, reel: null, bait: null, line: null, tackle: null };
          } else if (buddy.equippedGear.lure !== undefined) {
            buddy.equippedGear.tackle = buddy.equippedGear.lure;
            delete buddy.equippedGear.lure;
          }
          // Ensure tackle key exists
          if (buddy.equippedGear && buddy.equippedGear.tackle === undefined) {
            buddy.equippedGear.tackle = null;
          }
        });
      }
    },
  },
  {
    version: 8,
    description: 'Add trap state',
    migrate: (data) => {
      if (!data.playerState.trapState) {
        data.playerState.trapState = {
          status: 'empty',
          soakStartedAt: null,
          soakDurationMs: 0,
          readyAt: null,
          cooldownEndsAt: null,
          selectedTrapBait: null,
          totalTrapsSet: 0,
          totalTrapsPulled: 0,
          totalCreaturesCaught: 0,
          totalTrapValueEarned: 0,
        };
      }
    },
  },
];

/**
 * Run all pending migrations on a save data object.
 * Mutates saveData in place.
 * Returns the number of migrations applied.
 */
export function runMigrations(saveData: any): number {
  const savedVersion = saveData.version || 1;
  let applied = 0;

  for (const migration of MIGRATIONS) {
    if (migration.version > savedVersion) {
      console.log(`\uD83D\uDCBE Running migration v${migration.version}: ${migration.description}`);
      migration.migrate(saveData);
      applied++;
    }
  }

  if (applied > 0) {
    saveData.version = CURRENT_SAVE_VERSION;
    console.log(`\uD83D\uDCBE Save migrated from v${savedVersion} to v${CURRENT_SAVE_VERSION} (${applied} migration${applied === 1 ? '' : 's'})`);
  }

  return applied;
}
